import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as snackbarActions from "../../Redux/Actions/SnackbarActions/SnackbarActions";
import {
  Tabs, Tab, Box, Card, CardContent, Badge, Typography,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, TextField, Tooltip, Button, makeStyles
} from "@material-ui/core";
import { Link } from "react-router-dom";
import NotificationsIcon from "@material-ui/icons/Notifications";
import VisibilityIcon from "@material-ui/icons/Visibility";
import config from "../../Helpers/config";

// Your brand color
const BRAND_COLOR = "#107c8e";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(4),
    backgroundColor: "#f5fafa",
    minHeight: "100vh",
    fontFamily: "'Segoe UI', Roboto, sans-serif",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
  },
  title: {
    fontSize: "1.9rem",
    fontWeight: 700,
    color: BRAND_COLOR,
  },
  bellBadge: {
    "& .MuiBadge-badge": {
      backgroundColor: "#e74c3c",
      color: "white",
      fontWeight: "bold",
      fontSize: "0.8rem",
    },
  },
  searchBox: {
    marginBottom: theme.spacing(3),
    "& .MuiOutlinedInput-root": {
      borderRadius: 14,
      backgroundColor: "white",
      boxShadow: "inset 0 2px 10px rgba(0,0,0,0.08)",
      fontSize: "1.05rem",
      "&:hover": { boxShadow: "inset 0 2px 14px rgba(0,0,0,0.12)" },
    },
  },
  tabs: {
    marginBottom: theme.spacing(3),
    "& .MuiTab-root": {
      textTransform: "none",
      fontWeight: 600,
      fontSize: "1rem",
    },
    "& .MuiTabs-indicator": {
      backgroundColor: BRAND_COLOR,
      height: 4,
    },
  },
  card: {
    borderRadius: 18,
    boxShadow: "0 12px 32px rgba(16,124,142,0.12)",
    border: `1px solid #a0d9e4`,
    overflow: "hidden",
  },
  tableHead: {
    backgroundColor: "#e8f4f8",
    "& th": {
      fontWeight: 700,
      color: BRAND_COLOR,
      textTransform: "uppercase",
      fontSize: "0.85rem",
      letterSpacing: "0.8px",
      borderBottom: `3px solid ${BRAND_COLOR}`,
    },
  },
  tableRow: {
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: "#d0edf4 !important",
      transform: "translateY(-3px)",
      boxShadow: "0 10px 25px rgba(16,124,142,0.18)",
    },
  },
  amount: {
    fontWeight: 700,
    color: BRAND_COLOR,
    fontSize: "1.1rem",
  },
  chip: {
    fontWeight: 600,
    borderRadius: 20,
    padding: "4px 12px",
  },
  actionBtn: {
    minWidth: 40,
    padding: "8px 12px",
    borderRadius: 10,
    textTransform: "none",
    fontWeight: 600,
    margin: theme.spacing(0.5),
    transition: "all 0.3s",
    "&:hover": {
      transform: "translateY(-3px)",
      boxShadow: "0 8px 18px rgba(0,0,0,0.22)",
    },
  },
  btnView: { backgroundColor: "#0984e3", color: "white", "&:hover": { backgroundColor: "#0773c4" } },
  btnReceipt: { backgroundColor: "#27ae60", color: "white", "&:hover": { backgroundColor: "#1e8449" } },
  btnBig: { backgroundColor: "#636e72", color: "white", fontSize: "0.8rem", "&:hover": { backgroundColor: "#4d565a" } },
  pagination: {
    marginTop: theme.spacing(3),
    display: "flex",
    justifyContent: "space-between",
  },
  emptyState: {
    textAlign: "center",
    padding: "80px 20px",
    color: "#636e72",
    fontSize: "1.3rem",
    fontStyle: "italic",
  },
}));

const formatAmount = (amount) => {
  return Number(amount).toLocaleString("en-UG", {
    style: "currency",
    currency: "UGX",
    minimumFractionDigits: 0,
  });
};

const DispenserSales = ({ actions }) => {
  const classes = useStyles();

  const [pendingSales, setPendingSales] = useState([]);
  const [approvedSales, setApprovedSales] = useState([]);
  const [pendingPage, setPendingPage] = useState(1);
  const [approvedPage, setApprovedPage] = useState(1);
  const [pendingCount, setPendingCount] = useState(0);
  const [tabValue, setTabValue] = useState(0);
  const [searchText, setSearchText] = useState("");

  const pageSize = 10;

  // Fetch Pending Sales
  useEffect(() => {
    const fetchPending = async () => {
      try {
        const res = await fetch(
          `${config.epharmUrl}/Cinvoice/dispenser/invoices?status=pending&page=${pendingPage}&page_size=${pageSize}`
        );
        const data = await res.json();
        setPendingSales(data.data?.invoices || []);
        setPendingCount(data.data?.total_records || 0);
      } catch {
        actions.snackbarActions.errorSnackbar("Failed to load pending sales");
      }
    };
    fetchPending();
  }, [pendingPage, actions]);

  // Fetch Approved Sales
  useEffect(() => {
    const fetchApproved = async () => {
      try {
        const res = await fetch(
          `${config.epharmUrl}/Cinvoice/dispenser/invoices?status=approved&page=${approvedPage}&page_size=${pageSize}`
        );
        const data = await res.json();
        setApprovedSales(data.data?.invoices || []);
      } catch {
        actions.snackbarActions.errorSnackbar("Failed to load approved sales");
      }
    };
    fetchApproved();
  }, [approvedPage, actions]);

  const handleTabChange = (e, newValue) => setTabValue(newValue);

  const filteredPending = pendingSales.filter(s =>
    [s.invoice_number, s.customer_name].join(" ").toLowerCase().includes(searchText.toLowerCase())
  );

  const filteredApproved = approvedSales.filter(s =>
    [s.invoice_number, s.customer_name].join(" ").toLowerCase().includes(searchText.toLowerCase())
  );

  const renderStatus = (status) => {
    switch (status) {
      case 0: return <Chip label="Pending Approval" color="secondary" className={classes.chip} size="small" />;
      case 1: return <Chip label="Cash Paid" style={{ backgroundColor: "#27ae60", color: "white" }} className={classes.chip} size="small" />;
      case 3: return <Chip label="Credit Approved" style={{ backgroundColor: "#2980b9", color: "white" }} className={classes.chip} size="small" />;
      default: return <Chip label="Unknown" size="small" />;
    }
  };

  const renderActions = (sale, isApproved = false) => {
    const isCash = sale.status === 1;
    const invoiceNum = isCash ? sale.reciept_number : sale.invoice_number;
    const bigPath = isCash 
      ? `/receipt_print_big/${sale.customer_id}/${invoiceNum}/true`
      : `/invoice_print_big/${sale.customer_id}/${sale.invoice_number}/true`;

    return (
      <Box display="flex" gap={1} flexWrap="wrap">
        {!isApproved && !isCash && (
          <Tooltip title="View Invoice">
            <Link to={`/invoice_print_new/${sale.customer_id}/${sale.invoice_number}`}>
              <Button variant="contained" size="small" className={`${classes.actionBtn} ${classes.btnView}`}>
                <VisibilityIcon fontSize="small" />
              </Button>
            </Link>
          </Tooltip>
        )}

        {isCash && (
          <Tooltip title="View Receipt">
            <Link to={`/receipt_print_new/${sale.customer_id}/${invoiceNum}`}>
              <Button variant="contained" size="small" className={`${classes.actionBtn} ${classes.btnReceipt}`}>
                Receipt
              </Button>
            </Link>
          </Tooltip>
        )}

        <Tooltip title="Big Format">
          <Link to={bigPath}>
            <Button variant="contained" size="small" className={`${classes.actionBtn} ${classes.btnBig}`}>
              Big
            </Button>
          </Link>
        </Tooltip>
      </Box>
    );
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Typography className={classes.title}>Dispenser Sales</Typography>
        <Badge badgeContent={pendingCount} color="secondary" className={classes.bellBadge}>
          <NotificationsIcon style={{ fontSize: 36, color: BRAND_COLOR }} />
        </Badge>
      </Box>

      <TextField
        fullWidth
        variant="outlined"
        placeholder="Search by invoice or customer name..."
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        className={classes.searchBox}
      />

      <Tabs value={tabValue} onChange={handleTabChange} className={classes.tabs}>
        <Tab label={`Pending Sales (${pendingCount})`} />
        <Tab label="Approved Sales" />
      </Tabs>

      {/* Pending Sales Tab */}
      {tabValue === 0 && (
        <Card className={classes.card}>
          <CardContent>
            {filteredPending.length === 0 ? (
              <Typography className={classes.emptyState}>
                {searchText ? "No matching sales found" : "No pending sales at the moment"}
              </Typography>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow className={classes.tableHead}>
                        <TableCell>Invoice</TableCell>
                        <TableCell>Customer</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredPending.map((sale) => (
                        <TableRow key={sale.invoice_id} hover className={classes.tableRow}>
                          <TableCell><strong>{sale.invoice_number}</strong></TableCell>
                          <TableCell>{sale.customer_name || "-"}</TableCell>
                          <TableCell className={classes.amount}>{formatAmount(sale.total_amount)}</TableCell>
                          <TableCell>{renderStatus(sale.status)}</TableCell>
                          <TableCell>{sale.transaction_date}</TableCell>
                          <TableCell>{renderActions(sale)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box className={classes.pagination}>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={pendingPage === 1}
                    onClick={() => setPendingPage(p => Math.max(1, p - 1))}
                  >
                    Previous
                  </Button>
                  <Typography variant="body2" style={{ alignSelf: "center" }}>
                    Page {pendingPage}
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={filteredPending.length < pageSize}
                    onClick={() => setPendingPage(p => p + 1)}
                  >
                    Next
                  </Button>
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Approved Sales Tab */}
      {tabValue === 1 && (
        <Card className={classes.card}>
          <CardContent>
            {filteredApproved.length === 0 ? (
              <Typography className={classes.emptyState}>
                No approved sales found
              </Typography>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow className={classes.tableHead}>
                        <TableCell>Document</TableCell>
                        <TableCell>Customer</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredApproved.map((sale) => (
                        <TableRow key={sale.invoice_id} hover className={classes.tableRow}>
                          <TableCell>
                            <strong>{sale.status === 1 ? sale.reciept_number : sale.invoice_number}</strong>
                          </TableCell>
                          <TableCell>{sale.customer_name || "-"}</TableCell>
                          <TableCell className={classes.amount}>{formatAmount(sale.total_amount)}</TableCell>
                          <TableCell>{renderStatus(sale.status)}</TableCell>
                          <TableCell>{sale.transaction_date}</TableCell>
                          <TableCell>{renderActions(sale, true)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box className={classes.pagination}>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={approvedPage === 1}
                    onClick={() => setApprovedPage(p => Math.max(1, p - 1))}
                  >
                    Previous
                  </Button>
                  <Typography variant="body2" style={{ alignSelf: "center" }}>
                    Page {approvedPage}
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={filteredApproved.length < pageSize}
                    onClick={() => setApprovedPage(p => p + 1)}
                  >
                    Next
                  </Button>
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

const mapDispatchToProps = (dispatch) => ({
  actions: { snackbarActions: bindActionCreators(snackbarActions, dispatch) },
});

export default connect(null, mapDispatchToProps)(DispenserSales);